import React from 'react';
import Navigation from '@/components/shared/Navigation';
import Footer from '@/components/shared/Footer';
import { Warehouse, Building, ShoppingBag, Home, Layers, CreditCard, Briefcase } from 'lucide-react';

const IndustriesPage = () => {
  const industries = [
    {
      icon: Warehouse,
      name: 'Industrial & Logistics',
      description: 'Comprehensive cash flow modeling and lease-level analysis for warehouse, distribution, and logistics facilities.'
    },
    {
      icon: Building,
      name: 'Office',
      description: 'Detailed underwriting support for office acquisitions, including tenant credit analysis and lease term reconciliation.'
    },
    {
      icon: ShoppingBag,
      name: 'Retail',
      description: 'Rent roll validation, tenant sales analysis, and lease abstraction for retail centers and single-tenant properties.'
    },
    {
      icon: Home,
      name: 'Multifamily',
      description: 'Pro forma development and market analysis for multifamily investments, including rent growth projections and expense modeling.'
    },
    {
      icon: Layers,
      name: 'Mixed-Use',
      description: 'Complex financial modeling for multi-component assets, with separate cash flow analysis by use type.'
    },
    {
      icon: CreditCard,
      name: 'Credit & Debt Underwriting',
      description: 'Lender-focused analysis including debt service coverage, loan sizing, and risk assessment for commercial mortgages.'
    },
    {
      icon: Briefcase,
      name: 'Private Equity & Investment Firms',
      description: 'Portfolio-level support for fund acquisitions, including IC memo preparation and investment committee materials.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#F9FAFB] font-sans">
      <Navigation />

      {/* Header */}
      <section className="bg-white py-16 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-4xl font-semibold text-[#0A2540] mb-4">
            Industries Served
          </h1>
          <p className="text-xl text-[#2E2E2E] max-w-3xl">
            We provide specialized financial analysis and support across all major commercial real estate asset classes and investment strategies.
          </p>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <div 
                  key={index}
                  className="bg-white p-8 rounded-lg border border-gray-200 hover:border-[#4A6FA5] transition-colors"
                >
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-[#0A2540] rounded mr-4">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-[#0A2540]">
                      {industry.name}
                    </h3>
                  </div>
                  <p className="text-[#2E2E2E] text-sm leading-relaxed">
                    {industry.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Context */}
      <section className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-semibold text-[#0A2540] mb-8 text-center">
            Asset-Specific Expertise
          </h2>
          <div className="space-y-6 text-lg text-[#2E2E2E] leading-relaxed">
            <p>
              Our analysts are trained on asset-specific underwriting conventions, lease structures, 
              and market dynamics across all major property types. Each engagement is customized to match 
              client-specific modeling standards and investment criteria.
            </p>
            <p>
              We work with acquisitions teams on deal underwriting, asset managers on portfolio monitoring, 
              and lenders on credit analysis — adapting our deliverables to meet the requirements of each role.
            </p>
            <p>
              From single-asset acquisitions to portfolio-level transactions, our teams provide the analytical 
              support needed to move quickly through due diligence and closing.
            </p>
          </div>
        </div>
      </section>

      {/* Capabilities by Stage */}
      <section className="bg-[#F9FAFB] py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-semibold text-[#0A2540] mb-12 text-center">
            Support Across the Investment Lifecycle
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg border border-gray-200">
              <h3 className="text-xl font-semibold text-[#0A2540] mb-4">Acquisition Phase</h3>
              <ul className="space-y-2 text-[#2E2E2E]">
                <li className="flex items-start">
                  <span className="text-[#4A6FA5] mr-2">•</span>
                  <span>Pro forma modeling and returns analysis</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#4A6FA5] mr-2">•</span>
                  <span>Lease abstraction and rent roll validation</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#4A6FA5] mr-2">•</span>
                  <span>Market research and comps analysis</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#4A6FA5] mr-2">•</span>
                  <span>Investment committee materials</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg border border-gray-200">
              <h3 className="text-xl font-semibold text-[#0A2540] mb-4">Asset Management</h3>
              <ul className="space-y-2 text-[#2E2E2E]">
                <li className="flex items-start">
                  <span className="text-[#4A6FA5] mr-2">•</span>
                  <span>Portfolio monitoring and reporting</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#4A6FA5] mr-2">•</span>
                  <span>Budget vs. actual variance analysis</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#4A6FA5] mr-2">•</span>
                  <span>Lease expiration and rollover tracking</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#4A6FA5] mr-2">•</span>
                  <span>Property-level cash flow updates</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg border border-gray-200">
              <h3 className="text-xl font-semibold text-[#0A2540] mb-4">Disposition</h3>
              <ul className="space-y-2 text-[#2E2E2E]">
                <li className="flex items-start">
                  <span className="text-[#4A6FA5] mr-2">•</span>
                  <span>Exit modeling and sensitivity analysis</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#4A6FA5] mr-2">•</span>
                  <span>Marketing package preparation</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#4A6FA5] mr-2">•</span>
                  <span>Rent roll and lease abstract updates</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#4A6FA5] mr-2">•</span>
                  <span>Due diligence response support</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IndustriesPage;
